import { format, formatDistanceToNow } from 'date-fns';

const AGENT_AVATAR_GRADIENTS = [
  'bg-gradient-to-br from-red-500 to-red-600',
  'bg-gradient-to-br from-orange-500 to-orange-600',
  'bg-gradient-to-br from-amber-500 to-amber-600',
  'bg-gradient-to-br from-yellow-500 to-yellow-600',
  'bg-gradient-to-br from-lime-500 to-lime-600',
  'bg-gradient-to-br from-emerald-500 to-emerald-600',
  'bg-gradient-to-br from-teal-500 to-teal-600',
  'bg-gradient-to-br from-cyan-500 to-cyan-600',
  'bg-gradient-to-br from-sky-500 to-sky-600',
  'bg-gradient-to-br from-indigo-500 to-indigo-600',
  'bg-gradient-to-br from-fuchsia-500 to-fuchsia-600',
  'bg-gradient-to-br from-rose-500 to-rose-600',
];

const AGENT_TEXT_COLORS = [
  'text-red-400',
  'text-orange-400',
  'text-amber-400',
  'text-yellow-400',
  'text-lime-400',
  'text-emerald-400',
  'text-teal-400',
  'text-cyan-400',
  'text-sky-400',
  'text-indigo-400',
  'text-fuchsia-400',
  'text-rose-400',
];

function hashAgentId(agentId: string): number {
  let hash = 0;
  for (let index = 0; index < agentId.length; index += 1) {
    hash = ((hash << 5) - hash + agentId.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
}

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 Bytes';
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    sizes.length - 1,
  );
  return `${parseFloat((bytes / 1024 ** index).toFixed(1))} ${sizes[index]}`;
}

export function formatMessageTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return format(date, 'h:mm a');
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }
  if (date.getFullYear() === now.getFullYear()) {
    return format(date, 'MMM d');
  }
  return format(date, 'MMM d, yyyy');
}

export function formatRelativeTime(timestamp: string): string {
  return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
}

export function getFileIcon(filename: string): string {
  const extension = filename.split('.').pop()?.toLowerCase();
  const iconMap: Record<string, string> = {
    pdf: 'PDF',
    doc: 'DOC',
    docx: 'DOC',
    xls: 'XLS',
    xlsx: 'XLS',
    csv: 'CSV',
    ppt: 'PPT',
    pptx: 'PPT',
    png: '🖼️',
    jpg: '🖼️',
    jpeg: '🖼️',
    gif: '🖼️',
    webp: '🖼️',
    svg: '🖼️',
    txt: 'TXT',
    md: 'MD',
    json: 'JSON',
    zip: 'ZIP',
    rar: 'ZIP',
    tar: 'ZIP',
    default: '📄',
  };
  return iconMap[extension || 'default'] || iconMap.default;
}

export function getAgentAvatarClass(agentId: string): string {
  if (!agentId) return 'bg-gradient-to-br from-slate-500 to-slate-600';
  return AGENT_AVATAR_GRADIENTS[
    hashAgentId(agentId.toLowerCase()) % AGENT_AVATAR_GRADIENTS.length
  ];
}

export function getAgentTextColorClass(agentId: string): string {
  if (!agentId) return 'text-slate-400';
  return AGENT_TEXT_COLORS[
    hashAgentId(agentId.toLowerCase()) % AGENT_TEXT_COLORS.length
  ];
}

export function truncateText(text: string, length = 100): string {
  if (text.length <= length) return text;
  return `${text.slice(0, length).trim()}...`;
}

export function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');
}

export function generateConversationTitle(firstMessage: string): string {
  return truncateText(firstMessage.replace(/\s+/g, ' ').trim(), 50);
}

export function debounce<T extends (...args: any[]) => void>(fn: T, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const debounced = (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
  debounced.cancel = () => {
    if (timeoutId) clearTimeout(timeoutId);
  };
  return debounced as T & { cancel: () => void };
}
